import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Bell, Check, ArrowUpRight, Send, X, AlertTriangle, Info, User as UserIcon, Clock, Filter, Hand, FileDown } from "lucide-react";
import { toast } from "sonner";
import { NavLink, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, useHasPerm } from "@/context/AuthContext";
import UsageTip from "@/components/UsageTip";
import DescargarGuiaMenu from "@/components/DescargarGuiaMenu";
import { getNotifMeta, toneClasses, humanTime, agruparPorTiempo, routeForNotif } from "@/lib/notifStyles";

const CATEGORIAS = [
  { k: "todas",       label: "Todas",       tones: [] },
  { k: "urgentes",    label: "Urgentes",    tones: [] }, // filtro especial: critica=true
  { k: "stock",       label: "Stock",       tones: ["stock"] },
  { k: "precio",      label: "Precios",     tones: ["precio"] },
  { k: "pedido",      label: "Pedidos",     tones: ["pedido"] },
  { k: "pago",        label: "Pagos",       tones: ["pago"] },
  { k: "excepcion",   label: "Excepciones", tones: ["excepcion"] },
  { k: "comunicado",  label: "Comunicados", tones: ["comunicado"] },
];

export default function NotificacionesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canTomarP = useHasPerm("despachos.tomar");
  const canGestP = useHasPerm("despachos.gestionar");
  const canTomar = canTomarP || canGestP;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [estado, setEstado] = useState("todas"); // todas | no_leidas
  const [guiaMenu, setGuiaMenu] = useState(null);
  const [categoria, setCategoria] = useState("todas");
  const [openBroadcast, setOpenBroadcast] = useState(false);
  const [broadcast, setBroadcast] = useState({ titulo: "", descripcion: "", target: "all", critica: false });

  const load = useCallback(async () => {
    const { data } = await api.get(`/notificaciones?solo_no_leidas=${estado === "no_leidas"}`);
    setItems(data.items);
    setNoLeidas(data.no_leidas);
  }, [estado]);
  useEffect(() => { load(); }, [load]);

  const marcarUna = async (id) => {
    await api.post(`/notificaciones/${id}/leer`);
    setItems((xs) => xs.map((n) => n.id === id ? { ...n, leida: true } : n));
    setNoLeidas((u) => Math.max(0, u - 1));
  };
  const marcarTodas = async () => {
    await api.post("/notificaciones/leer-todas");
    toast.success("Todas marcadas como leídas");
    load();
  };

  const enviarComunicado = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notificaciones/comunicado", broadcast);
      toast.success("Comunicado enviado al equipo");
      setOpenBroadcast(false);
      setBroadcast({ titulo: "", descripcion: "", target: "all", critica: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar"); }
  };

  // Aplicar filtro de categoría
  const filtered = useMemo(() => {
    const cat = CATEGORIAS.find((c) => c.k === categoria);
    if (!cat || cat.k === "todas") return items;
    if (cat.k === "urgentes") return items.filter((n) => n.critica);
    return items.filter((n) => cat.tones.includes(getNotifMeta(n.tipo).tone));
  }, [items, categoria]);

  const groups = agruparPorTiempo(filtered);

  const openNotif = (n) => {
    if (!n.leida) marcarUna(n.id);
    const t = routeForNotif(n);
    if (t) navigate(t);
  };

  const tomarPedido = async (n) => {
    if (!n.entity_id) return;
    try {
      await api.post(`/pedidos/${n.entity_id}/tomar`);
      toast.success("Pedido tomado 🙋");
      if (!n.leida) marcarUna(n.id);
      navigate(`/pedidos?open=${n.entity_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo tomar");
    }
  };

  const descargarGuia = (n) => {
    const pid = n.entity_id;
    if (!pid) return;
    setGuiaMenu({ pedidoId: pid, notif: n });
  };

  const descargarRecibo = async (n) => {
    const batch = n.entity_id || n?.data?.batch_id;
    if (!batch) return;
    try {
      const { data } = await api.get(`/reportes/liquidacion-comisiones/${batch}/estado-cuenta`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `mi_recibo_${batch}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      if (!n.leida) marcarUna(n.id);
      toast.success("Recibo descargado ✓");
    } catch (err) {
      toast.error("No se pudo descargar el recibo");
    }
  };

  // Counts por categoría (para badges en los chips)
  const counts = useMemo(() => {
    const c = { todas: items.length, urgentes: items.filter((n) => n.critica).length };
    CATEGORIAS.forEach((cat) => {
      if (cat.k === "todas" || cat.k === "urgentes") return;
      c[cat.k] = items.filter((n) => cat.tones.includes(getNotifMeta(n.tipo).tone)).length;
    });
    return c;
  }, [items]);

  return (
    <div>
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Centro de comunicación</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
            <Bell className="w-8 h-8 text-primary" /> Bandeja de avisos
          </h1>
          <p className="text-muted-foreground mt-2">
            {noLeidas > 0
              ? <>Tienes <span className="text-primary font-semibold">{noLeidas} sin leer</span>. Los urgentes tienen banda rosa.</>
              : "Todo al día. Aquí queda registro de todo lo que pasa en Beem."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button onClick={() => setOpenBroadcast(true)} variant="outline" className="h-11" data-testid="new-comunicado-btn">
              <Send className="w-4 h-4 mr-2" /> Enviar comunicado
            </Button>
          )}
          {noLeidas > 0 && (
            <Button onClick={marcarTodas} className="bg-primary hover:bg-primary/90 h-11" data-testid="leer-todas-btn">
              <Check className="w-4 h-4 mr-2" /> Marcar todas leídas
            </Button>
          )}
        </div>
      </header>

      <UsageTip storageKey="notificaciones" steps={[
        "Toda información oficial (cambios de precio, stock, pedidos, comunicados) queda registrada aquí.",
        "Las <b>urgentes</b> aparecen con banda rosa y te bloquean al iniciar sesión hasta que las confirmes.",
        "Haz clic en cualquier aviso para saltar directo al pedido, producto o pago relacionado.",
      ]} />

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-1 bg-card border border-border rounded-lg p-1 w-fit">
          {[
            { k: "todas", label: "Todas" },
            { k: "no_leidas", label: `Sin leer${noLeidas > 0 ? ` · ${noLeidas}` : ""}` },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setEstado(f.k)}
              className={`px-4 h-9 rounded-md text-sm font-medium transition-colors ${estado === f.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
              data-testid={`estado-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          {CATEGORIAS.map((c) => {
            const active = categoria === c.k;
            const n = counts[c.k] || 0;
            return (
              <button
                key={c.k}
                onClick={() => setCategoria(c.k)}
                data-testid={`categoria-${c.k}`}
                className={`h-8 px-3 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent border-border text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {c.label}
                {n > 0 && <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] tabular-nums ${active ? "bg-background/20" : "bg-muted"}`}>{n}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista agrupada por tiempo */}
      <div className="space-y-6">
        {filtered.length === 0 && (
          <div className="py-20 text-center border border-dashed border-border rounded-xl bg-card/40">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="text-base font-medium text-foreground">Nada por aquí</div>
            <div className="text-sm text-muted-foreground mt-1">No hay avisos en este filtro.</div>
          </div>
        )}
        {groups.map((g) => (
          <div key={g.label}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 font-bold mb-2 px-1">{g.label}</div>
            <div className="space-y-2">
              {g.items.map((n) => (
                <NotifCard
                  key={n.id}
                  n={n}
                  canTomar={canTomar}
                  onOpen={() => openNotif(n)}
                  onMark={() => marcarUna(n.id)}
                  onTomar={() => tomarPedido(n)}
                  onDescargarRecibo={() => descargarRecibo(n)}
                  onDescargarGuia={() => descargarGuia(n)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={openBroadcast} onOpenChange={setOpenBroadcast}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Enviar comunicado interno</DialogTitle>
            <p className="text-sm text-muted-foreground">Aparecerá en la bandeja del destinatario. Si es urgente, deberá confirmarlo al iniciar sesión.</p>
          </DialogHeader>
          <form onSubmit={enviarComunicado} className="space-y-4 mt-4">
            <div>
              <Label>Título*</Label>
              <Input required value={broadcast.titulo} onChange={(e) => setBroadcast({ ...broadcast, titulo: e.target.value })} className="h-11 mt-1" placeholder="Ej: Cambio en política de descuentos" data-testid="broadcast-titulo" />
            </div>
            <div>
              <Label>Mensaje*</Label>
              <textarea required value={broadcast.descripcion} onChange={(e) => setBroadcast({ ...broadcast, descripcion: e.target.value })} className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm min-h-[120px]" placeholder="Escribe el mensaje que el equipo debe leer…" data-testid="broadcast-descripcion" />
            </div>
            <div>
              <Label>¿A quién?</Label>
              <div className="grid grid-cols-3 gap-1 mt-1">
                {[
                  { k: "all", label: "Todos" },
                  { k: "vendedores", label: "Vendedores" },
                  { k: "directors", label: "Directores" },
                ].map((o) => (
                  <button key={o.k} type="button" onClick={() => setBroadcast({ ...broadcast, target: o.k })} className={`h-11 rounded-md border text-sm font-medium transition-colors ${broadcast.target === o.k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}>{o.label}</button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-rose-500/20 bg-rose-500/5">
              <input type="checkbox" checked={broadcast.critica} onChange={(e) => setBroadcast({ ...broadcast, critica: e.target.checked })} className="w-4 h-4 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-foreground flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> Marcar como urgente</div>
                <div className="text-xs text-muted-foreground mt-0.5">Bloquea al destinatario al iniciar sesión hasta que confirme haberlo leído.</div>
              </div>
            </label>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="broadcast-enviar">
              <Send className="w-4 h-4 mr-2" /> Enviar comunicado
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {guiaMenu && (
        <DescargarGuiaMenu
          pedidoId={guiaMenu.pedidoId}
          nombreArchivo={guiaMenu.pedidoId.slice(0, 8)}
          onDone={() => { if (guiaMenu.notif && !guiaMenu.notif.leida) marcarUna(guiaMenu.notif.id); }}
          onClose={() => setGuiaMenu(null)}
        />
      )}
    </div>
  );
}

function NotifCard({ n, onOpen, onMark, onTomar, canTomar, onDescargarRecibo, onDescargarGuia }) {
  const meta = getNotifMeta(n.tipo);
  const tone = toneClasses(meta.tone, n.critica);
  const Icon = meta.icon;
  const target = routeForNotif(n);
  // Feb/2026: solo mostrar "Tomar" si la notif viene de un pedido CONFIRMADO.
  // Los borradores están en manos del vendedor; el backend ya los rechaza con
  // 403, pero ocultar el botón evita confusión al operador.
  const notifEstado = n?.data?.estado;
  const puedeTomar = canTomar
    && n.tipo === "pedido.creado"
    && n.entity_id
    && (notifEstado === "confirmado" || !notifEstado); // retrocompatible con notifs viejas sin estado
  const puedeDescargarRecibo = n.tipo === "comisiones.liquidadas" && (n.entity_id || n?.data?.batch_id);
  const puedeDescargarGuia = n.tipo === "pedido.despachado" && n.entity_id && n?.data?.guia_disponible;

  return (
    <div
      className={`bg-card border rounded-xl p-4 transition-all relative overflow-hidden ${
        n.critica ? "border-rose-500/40 shadow-[0_0_0_1px_theme(colors.rose.400/20%)]" :
        !n.leida ? "border-primary/25" : "border-border"
      } ${target ? "hover:border-primary/50 hover:shadow-sm cursor-pointer" : ""}`}
      onClick={target ? onOpen : undefined}
      data-testid={`notif-${n.id}`}
    >
      {n.critica && <div className="absolute top-0 left-0 w-1 h-full bg-rose-500" />}
      {!n.leida && !n.critica && <div className="absolute top-0 left-0 w-1 h-full bg-primary" />}

      <div className="flex items-start gap-4">
        {/* Icono */}
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tone.iconBg}`}>
          <Icon className={`w-5 h-5 ${tone.iconFg}`} />
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${tone.chip}`}>
              {meta.label}
            </span>
            {n.critica && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-rose-500 text-white flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Urgente</span>}
            {!n.leida && <span className="text-[10px] text-primary font-semibold">● Sin leer</span>}
          </div>

          <div className={`text-[15px] leading-snug ${!n.leida ? "font-semibold text-foreground" : "text-foreground/85"}`}>
            {n.titulo}
          </div>
          {n.descripcion && <div className="text-[13px] text-muted-foreground mt-1 leading-relaxed">{n.descripcion}</div>}

          {/* Meta */}
          <div className="mt-2.5 flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground/85">
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {humanTime(n.at || n.created_at)}</span>
            {n.actor_nombre && <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {n.actor_nombre}</span>}
            {n.motivo && <span className="inline-flex items-center gap-1"><Info className="w-3 h-3" /> {n.motivo}</span>}
            {n.vigencia && <span>Vigencia: {new Date(n.vigencia).toLocaleDateString("es-CO")}</span>}
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          {puedeTomar && (
            <button
              onClick={(e) => { e.stopPropagation(); onTomar(); }}
              className="h-8 px-3 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 btn-tactile whitespace-nowrap"
              title="Reclamar este pedido para prepararlo"
              data-testid={`notif-tomar-${n.id}`}
            >
              <Hand className="w-3 h-3" /> Tomar
            </button>
          )}
          {puedeDescargarRecibo && (
            <button
              onClick={(e) => { e.stopPropagation(); onDescargarRecibo(); }}
              className="h-8 px-3 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 btn-tactile whitespace-nowrap"
              title="Descargar mi recibo (estado de cuenta PDF)"
              data-testid={`notif-recibo-${n.id}`}
            >
              <FileDown className="w-3 h-3" /> Descargar mi recibo
            </button>
          )}
          {puedeDescargarGuia && (
            <button
              onClick={(e) => { e.stopPropagation(); onDescargarGuia(); }}
              className="h-8 px-3 rounded-full text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1 btn-tactile whitespace-nowrap"
              title="Ver guía de transporte"
              data-testid={`notif-guia-${n.id}`}
            >
              <FileDown className="w-3 h-3" /> Ver guía
            </button>
          )}
          {!n.leida && (
            <button
              onClick={(e) => { e.stopPropagation(); onMark(); }}
              className="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
              title="Marcar como leída"
              data-testid={`marcar-${n.id}`}
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          {target && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              className="text-[11px] text-primary hover:underline font-semibold whitespace-nowrap flex items-center gap-0.5"
            >
              {meta.accion} <ArrowUpRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
